import { Pagination as PagProps } from "@/types";
import {
  Pagination as ShadcnPagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
} from "@/components/ui/pagination";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

type PaginationProps = {
  data: PagProps;
  className?: string;
  type?: "default" | "custom";
  type_string?: string;
};

export function Pagination({
  data: { current_page, last_page, total },
  className,
  type = "default",
  type_string,
}: PaginationProps) {
  const { t } = useTranslation();
  const renderPageNumbers = () => {
    const pageNumbers: React.ReactNode[] = [];

    const searchParams = new URLSearchParams(window.location.search);

    const generatePageLink = (pageNum: number) => {

      if (type === "default") {
        searchParams.set("page", String(pageNum));
        searchParams.set("page", String(pageNum));
      } else if (type_string) {
        searchParams.delete(type_string)
        searchParams.set(type_string, String(pageNum));
      }
      return `?${searchParams.toString()}`;
    }

    const addPageNumber = (pageNum: number) => {
      pageNumbers.push(
        <PaginationItem key={pageNum}>
          <PaginationLink
            to={generatePageLink(pageNum)}
            className={cn(
              "flex size-10 items-center justify-center rounded-lg text-sm font-medium tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand",
              pageNum === current_page
                ? "bg-brand-soft text-brand hover:bg-brand-soft hover:text-brand"
                : "border border-app-border bg-app-surface text-app-fg hover:bg-app-surface-muted"
            )}
          >
            {pageNum}
          </PaginationLink>
        </PaginationItem>
      );
    };

    const addEllipsis = (key: string) => {
      pageNumbers.push(
        <PaginationItem key={key}>
          <span className="flex size-10 items-center justify-center text-app-fg-subtle">
            ...
          </span>
        </PaginationItem>
      );
    };

    // Sempre adiciona a primeira página
    addPageNumber(1);

    // Adiciona elipse se necessário
    if (current_page > 3) {
      addEllipsis("start-ellipsis");
    }

    // Adiciona a página anterior à atual, se for aplicável
    if (current_page > 2) {
      addPageNumber(current_page - 1);
    }

    // Adiciona a página atual
    if (current_page !== 1 && current_page !== last_page) {
      addPageNumber(current_page);
    }

    // Adiciona a página seguinte à atual, se for aplicável
    if (current_page < last_page - 1) {
      addPageNumber(current_page + 1);
    }

    // Adiciona elipse se necessário para as últimas páginas
    if (current_page < last_page - 2) {
      addEllipsis("end-ellipsis");
    }

    // Sempre adiciona a última página
    if (last_page !== 1) {
      addPageNumber(last_page);
    }

    return pageNumbers;
  };

  return (
    <ShadcnPagination
      className={cn("mt-8 items-center justify-between", className)}
    >
      <p className="text-sm tabular-nums text-app-fg-muted">
        {" "}
        <strong className="font-medium">
          {t("pagination.totalRecords")}:
        </strong>{" "}
        {total}
      </p>
      {total <= 10 ? (
        <div />
      ) : (
        <PaginationContent className="flex items-center space-x-1">
          {renderPageNumbers()}
        </PaginationContent>
      )}
    </ShadcnPagination>
  );
}
